<!--
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：稼働実績分析グラフ
 *
 * 作成者：2021/11/30 ノア）黄
 * 最終修正者：2021/11/30 ノア）黄
 *
 -->
<template>
  <div class="content">
    <div id="canvasContainer" class="canvasContainer"></div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Emit, Watch } from 'vue-property-decorator'
import * as echarts from 'echarts/core'
import { BarChart, BarSeriesOption } from 'echarts/charts'
import { CallbackDataParams } from 'echarts/types/dist/shared'
import { LabelLayout } from 'echarts/features'
import { CanvasRenderer } from 'echarts/renderers'
import {
  TitleComponent,
  TitleComponentOption,
  TooltipComponent,
  TooltipComponentOption,
  GridComponent,
  GridComponentOption
} from 'echarts/components'

import { MoveResult, MoveResultItem } from '@/models'

export type ECOption = echarts.ComposeOption<
  TitleComponentOption | TooltipComponentOption | GridComponentOption | BarSeriesOption
>

@Component({
  name: 'ttiCharts'
})
export default class TtiCharts extends Vue {
  /**
   * グラフ
   */
  chartInstance!: echarts.ECharts

  /**
   * グラフデータ
   */
  chartData!: ECOption

  /**
   * 稼働実績データ
   */
  @Prop()
  data!: MoveResult

  /**
   * グラフの内容をクリックした時のコールバック
   *
   * @param {MoveResultItem} moveResult クリックしたデータ
   * @returns {MoveResultItem} クリックしたデータ
   */
  @Emit('onChartContextClick')
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  chartContextClickEmit(moveResult: MoveResultItem): any {
    const result = {
      moveResult: moveResult
    }
    return result
  }

  /**
   * 稼働実績データ変更した時の処理
   *
   * @param {MoveResult} value  稼働実績データ
   * @returns {void}
   */
  @Watch('data', { immediate: true, deep: true })
  onDataChange(value: MoveResult): void {
    this.$log.debug('TtiCharts > onDataChange', value)
    this.changeMoveResultDataToChartData()
  }

  /**
   * インスタンスがマウントされたちょうど後にフック処理
   *
   * @returns {void}
   */
  mounted(): void {
    this.$log.debug('TtiCharts > mounted')
    this.initChartConfig()
    this.initChart()
  }

  /**
   * 稼働実績データをグラグデータに変換
   *
   * @returns {void}
   */
  changeMoveResultDataToChartData(): void {
    this.$log.debug('TtiCharts > changeMoveResultDataToChartData')
    this.chartData = {
      tooltip: {},
      xAxis: {
        data: this.data.moveResult.map(item => item.label)
      },
      yAxis: {
        max: 100,
        min: 0
      },
      series: [
        {
          name: '稼働実績',
          type: 'bar',
          label: {
            show: true,
            position: 'top'
          },
          data: this.data.moveResult.map(item => item.result)
        }
      ]
    }

    this.chartInstance?.setOption(this.chartData)
  }

  /**
   * グラフコンフィグ初期化
   *
   * @returns {void}
   */
  initChartConfig(): void {
    echarts.use([BarChart, LabelLayout, CanvasRenderer, TitleComponent, TooltipComponent, GridComponent])
  }

  /**
   * グラフ作成
   *
   * @returns {void}
   */
  initChart(): void {
    this.chartInstance = echarts.init(document.getElementById('canvasContainer') as HTMLElement)
    this.chartInstance.setOption(this.chartData)
    this.chartInstance.on('click', this.onChartContextClick.bind(this))
    setTimeout(() => {
      window.onresize = () => {
        const echartsElement = document.getElementById('canvasContainer') as HTMLElement
        if (echartsElement?.offsetWidth > 0) {
          this.chartInstance.resize()
        }
      }
    }, 500)
  }

  /**
   * グラフの内容をクリックした時の処理
   *
   * @param {CallbackDataParams} params EChartのコールバックパラメータ
   * @returns {void}
   */
  onChartContextClick(params: CallbackDataParams): void {
    const clickedData = this.data.moveResult[params.dataIndex]
    this.$log.debug('TtiCharts > onChartContextClick, clickedData=', clickedData)
    this.chartContextClickEmit(clickedData)
  }
}
</script>

<style scoped lang="scss">
.content {
  width: 100%;
}

.canvasContainer {
  width: 100%;
  height: 75vh;
}

.pdfPrintBtn {
  z-index: 1000;
  position: absolute;
  right: 10px;
  background-color: #d9d9d9;
  color: #000;
}
</style>
